/**
 * Device Command Mapper
 *
 * Maps SensorControlConfig commands from editor control components
 * to Kasa device API calls. Uses the org slug from the device binding
 * so it works from any context (personal space, different org, etc.).
 */

import type { SensorControlConfig, SensorResponse, DeviceBinding } from "@/components/shared/rendering/InteractionContext";
import { workflowApi } from "@/services/api/workflowApi";
import { useOrganizationStore } from "@/stores/organization/organization-store";

function resolveOrgSlug(binding: DeviceBinding): string {
  if (binding.orgSlug) return binding.orgSlug;
  // Fallback: use current org context or first available org
  const orgState = useOrganizationStore.getState();
  return orgState.currentContext?.orgSlug
    || orgState.organizations[0]?.slug
    || '';
}

async function sendDeviceCommand(binding: DeviceBinding, action: string, body?: Record<string, unknown>): Promise<void> {
  const orgSlug = resolveOrgSlug(binding);
  if (!orgSlug) throw new Error('No organization context for device control');
  const url = `/api/orgs/${orgSlug}/devices/${encodeURIComponent(binding.deviceId)}/kasa/${action}`;
  await workflowApi.post(url, body);
}

/**
 * Execute a device command based on a sensor control event.
 */
export async function executeDeviceCommand(
  config: SensorControlConfig,
  binding: DeviceBinding
): Promise<SensorResponse> {
  const { command, value, property } = config.parameters || {};

  try {
    // Button press → toggle
    if (command === "press") {
      await sendDeviceCommand(binding, "toggle");
      return { success: true, timestamp: Date.now() };
    }

    // Explicit on/off from toggle switches
    if (command === "set" && (property === "isOn" || property === "isActive")) {
      await sendDeviceCommand(binding, value ? "on" : "off");
      return { success: true, timestamp: Date.now() };
    }

    // Numeric value → brightness (slider, dial, stepper)
    if (command === "set" && typeof value === "number") {
      if (binding.capabilities.brightness) {
        const brightness = Math.round(Math.min(100, Math.max(0, value)));
        await sendDeviceCommand(binding, "brightness", { brightness });
      } else {
        await sendDeviceCommand(binding, value > 0 ? "on" : "off");
      }
      return { success: true, timestamp: Date.now() };
    }

    // Release — no-op
    if (command === "release") {
      return { success: true, timestamp: Date.now() };
    }

    console.warn("[DeviceCommandMapper] Unhandled command:", command, config.parameters);
    return { success: true, timestamp: Date.now() };
  } catch (error) {
    const message = error instanceof Error ? error.message : "Device control failed";
    console.error("[DeviceCommandMapper] Error:", message);
    return { success: false, error: message, timestamp: Date.now() };
  }
}
